import type { User } from "@/app/types/user"
import {
  setToken,
  getToken,
  removeToken,
  setUser,
  getUser,
  removeUser,
  setOriginalAdmin,
  clearOriginalAdmin,
} from "./utils"
import { isClientRoleId } from "@/app/lib/auth/client-session-policy"
import { readJwtPayloadLoose } from "@/app/lib/auth/read-jwt-payload-client"
import { SESSION_HYDRATED_EVENT } from "@/components/providers/cross-tab-session-sync"

const API_URL = "/api/authentication"

type OtpRequiredLoginResponse = {
  success: true
  otpRequired: true
  challengeId: string
  user: Pick<User, "id" | "email" | "role">
}

type LoginSuccessResponse = {
  user: User
  token: string
  otpRequired?: false
}

export const authService = {
  async login(
    email: string,
    password: string,
    rememberMe = false,
  ): Promise<LoginSuccessResponse | OtpRequiredLoginResponse> {
    try {
      console.log("[authService.login] Logging in user:", email)

      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, rememberMe }),
      })

      console.log("[authService.login] Login response status:", response.status)

      if (!response.ok) {
        const errorData = await response.json()
        console.error("[authService.login] Login error:", errorData)
        throw new Error(errorData.error || `Login failed: ${response.status}`)
      }

      const data = await response.json()
      console.log("[authService.login] Login successful:", { userId: data.user?.id, email: data.user?.email })

      // Store token and user data only after final auth token is issued
      if (data.token && !data.otpRequired) {
        const roleId = data.user?.role?.id
        const persistRememberMe = isClientRoleId(roleId) ? false : rememberMe
        setToken(data.token, persistRememberMe, roleId)
        setUser(data.user, persistRememberMe, roleId)
      }

      return data
    } catch (error) {
      console.error("[authService.login] Error during login:", error)
      throw error
    }
  },

  async getCurrentUser(): Promise<User | null> {
    try {
      console.log("[authService.getCurrentUser] Checking current user...")

      const token = getToken()
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      }
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }

      // Cookie-only sessions (new tab without sessionStorage) still work via mtp_session.
      const response = await fetch(`${API_URL}/check`, {
        method: "GET",
        headers,
        credentials: "same-origin",
      })

      console.log("[authService.getCurrentUser] Auth check response status:", response.status)

      if (!response.ok) {
        console.log("[authService.getCurrentUser] Auth check failed, clearing tokens")
        removeToken()
        removeUser()
        return null
      }

      const data = await response.json()
      console.log("[authService.getCurrentUser] Auth check successful:", data.success)

      if (data.success && data.user && data.user.id && data.user.role) {
        const hydrateToken =
          typeof data.token === "string" && data.token.length > 0
            ? data.token
            : token
        const payload = hydrateToken ? readJwtPayloadLoose(hydrateToken) : null
        const roleId = data.user.role?.id
        const rememberMe = isClientRoleId(roleId)
          ? false
          : payload?.rm === true

        // New tabs share the cookie but not sessionStorage — restore token when missing.
        if (hydrateToken && !getToken()) {
          setToken(hydrateToken, rememberMe, roleId)
          if (typeof window !== "undefined") {
            window.dispatchEvent(new Event(SESSION_HYDRATED_EVENT))
          }
        }
        setUser(data.user, rememberMe, roleId)
        // Handle impersonation state
        if (data.user.isImpersonated && data.user.originalAdmin) {
          setOriginalAdmin(data.user.originalAdmin)
        } else {
          clearOriginalAdmin()
        }
        return data.user
      } else {
        console.log("[authService.getCurrentUser] Auth check failed or incomplete user, clearing stored data")
        removeToken()
        removeUser()
        clearOriginalAdmin()
        return null
      }
    } catch (error) {
      console.error("[authService.getCurrentUser] Error fetching current user:", error)
      // Clear invalid token
      removeToken()
      removeUser()
      return null
    }
  },

  logout(): void {
    console.log("[authService.logout] Logging out...")
    removeToken()
    removeUser()
    clearOriginalAdmin()
    void fetch(`${API_URL}/logout`, { method: "POST" }).catch(() => {
      /* non-blocking cookie clear */
    })
  },

  getStoredUser(): User | null {
    try {
      const user = getUser()
      console.log(
        "[authService.getStoredUser] Retrieved stored user:",
        user ? { id: user.id, email: user.email } : null,
      )
      return user
    } catch (error) {
      console.error("[authService.getStoredUser] Error:", error)
      return null
    }
  },

  getStoredToken(): string | null {
    try {
      const token = getToken()
      console.log(
        "[authService.getStoredToken] Retrieved stored token:",
        token ? "Present" : "Missing",
      )
      return token
    } catch (error) {
      console.error("[authService.getStoredToken] Error:", error)
      return null
    }
  },
}
